import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAppContext } from "../Context/AppContext";
import { isLoggedIn } from "../auth/authStorage";
import { isUnpaidExplorerUser } from "../explorer/isUnpaidExplorerUser";
import { isPaidPortalMember } from "../utils/membershipTier";
import { useGetUser } from "../api/queries/useGetUser";

/**
 * Explorer users without full membership: show sidebar shell but lock nav behind membership.
 * Paid Business/Executive/Early/Youth members use the normal sidebar behaviour.
 * `/register-user` stays full-width with no sidebar, except explorers upgrading to membership.
 */
export function useMembershipSidebarGate() {
  const { user, objectId } = useAppContext();
  const location = useLocation();
  const authed = isLoggedIn();

  const { data: userData } = useGetUser({
    id: objectId || "",
    enabled: authed && !!objectId,
  });
  const profile = userData?.user ?? user?.user;

  const isExplorer = useMemo(() => isUnpaidExplorerUser(profile), [profile]);

  const isCancelled = useMemo(() => {
    const st = profile?.stripeStatus?.toLowerCase?.();
    return st === "canceled" || st === "cancelled";
  }, [profile?.stripeStatus]);

  const hasPaidMembership = isPaidPortalMember(profile);
  const isRegistrationWizard = location.pathname === "/register-user";

  /** Membership wizard is full-width; explorers keep their sidebar while upgrading. */
  const hideSidebarOnRegistrationWizard = isRegistrationWizard && !isExplorer;

  const lockSidebarForMembership = useMemo(
    () =>
      authed &&
      isExplorer &&
      !hideSidebarOnRegistrationWizard,
    [authed, isExplorer, hideSidebarOnRegistrationWizard],
  );

  const treatAsSidebarMember = hideSidebarOnRegistrationWizard
    ? false
    : hasPaidMembership || isCancelled || lockSidebarForMembership;

  return {
    lockSidebarForMembership,
    treatAsSidebarMember,
    hideSidebarOnRegistrationWizard,
  };
}
